import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  ChangeEvent,
} from "react";
import { FixaInputField } from "@coworker/apprestructured/src/shared/wrappers/FixaInputField/FixaInputField";
import { FixaTextArea } from "@coworker/apprestructured/src/shared/wrappers/FixaTextArea/FixaTextArea";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import ListItem from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/ListItem/ListItem";
import InputWrapper from "@coworker/apprestructured/src/task-management/components/InputWrapper/InputWrapper";
import NewChecklistItem from "@coworker/apprestructured/src/task-management/components/CustomTaskDescription/NewChecklistItem/NewChecklistItem";
import ModalColumn from "@coworker/apprestructured/src/task-management/components/ModalColumn/ModalColumn";
import styles from "./CustomTaskDescription.module.css";
import ChecklistItem from "@coworker/apprestructured/src/task-management/types/ChecklistItem";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import ErrorAddOn from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/ListItem/ErrorAddOn/ErrorAddOn";
import { FixaModalBody } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalBody";
import StyledModalFooter from "../StyledModalFooter/StyledModalFooter";
import useSaveGuardedFunction from "@coworker/apprestructured/src/save-guard/useSaveGuardedFunction";
import { useTranslation } from "@coworker/locales";
import TrashCanIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/TrashCanIconPath";
import FixaSSRIcon from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/FixaSSRIcon";
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  closestCenter,
} from "@dnd-kit/core";
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import HandleIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/HandleIconPath";
import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import SmallPlusIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/SmallPlusIconPath";

const MAX_TITLE_LENGTH = 255;
const MAX_DESCRIPTION_LENGTH = 1000;
const MAX_CHECKLIST_ITEM_LENGTH = 255;
const MAX_CHECKLIST_ITEMS = 100;
const ADD_CHECKLIST_BUTTON_ID = "add-checklist-item-button";

const createChecklistItemId = (item: ChecklistItem, index: number) =>
  `${item.title}-${index}`;

export type CustomTaskDescriptionChange = Partial<{
  title: string;
  description: string;
  checklist: ChecklistItem[];
}>;

type CustomTaskDescriptionProps = {
  title: string;
  description: string;
  checklist: ChecklistItem[];
  disabled: boolean;
  showValidation: boolean;
  onConfirmClick: ({
    title,
    description,
    checklist,
  }: CustomTaskDescriptionChange) => void;
};

export function CustomTaskDescription({
  title,
  description,
  checklist,
  disabled,
  showValidation,
  onConfirmClick,
}: CustomTaskDescriptionProps) {
  const checklistInputRef = useRef<HTMLInputElement>(null);

  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState(title);
  const [modalDescription, setModalDescription] = useState(description);
  const [modalChecklist, setModalChecklist] = useState(checklist);
  const [modalChecklistInput, setModalChecklistInput] = useState("");
  const [showChecklistDraft, setShowChecklistDraft] = useState(false);
  const [showChecklistError, setShowChecklistError] = useState(false);
  const [hasClickedConfirmWithEmptyTitle, setHasClickedConfirmWithEmptyTitle] =
    useState(false);

  const { t } = useTranslation();

  const titleError = useMemo(() => {
    if (!modalTitle && (hasClickedConfirmWithEmptyTitle || showValidation)) {
      return t("pleaseFillInString");
    }
    if (modalTitle.length > MAX_TITLE_LENGTH) {
      return t("maximumCharactersLengthString", { count: MAX_TITLE_LENGTH });
    }
    return null;
  }, [hasClickedConfirmWithEmptyTitle, modalTitle, showValidation, t]);

  const descriptionError = useMemo(() => {
    if (modalDescription.length > MAX_DESCRIPTION_LENGTH) {
      return t("maximumCharactersLengthString", {
        count: MAX_DESCRIPTION_LENGTH,
      });
    }
    return null;
  }, [modalDescription, t]);

  const checklistItemError = useMemo(() => {
    if (showChecklistError && !modalChecklistInput.trim()) {
      return t("pleaseFillInString");
    }
    if (modalChecklistInput.length > MAX_CHECKLIST_ITEM_LENGTH) {
      return t("maximumCharactersLengthString", {
        count: MAX_CHECKLIST_ITEM_LENGTH,
      });
    }
    return null;
  }, [modalChecklistInput, showChecklistError, t]);

  const listViewError = useMemo(() => {
    if (!title && showValidation) {
      return t("pleaseFillInString");
    }
    return null;
  }, [showValidation, t, title]);

  const {
    saveGuardedFunction: handleCloseModalClick,
    enableSaveGuard,
    disableSaveGuard,
  } = useSaveGuardedFunction(
    useCallback(() => {
      setShowModal(false);
      setHasClickedConfirmWithEmptyTitle(false);
    }, [])
  );

  const handleConfirmClick = useCallback(() => {
    setHasClickedConfirmWithEmptyTitle(!modalTitle);
    if (modalTitle && !titleError && !descriptionError && !checklistItemError) {
      const change: CustomTaskDescriptionChange = {};
      if (modalTitle !== title) {
        change.title = modalTitle;
      }
      if (modalDescription !== description) {
        change.description = modalDescription;
      }
      if (modalChecklist !== checklist) {
        change.checklist = modalChecklist;
      }

      disableSaveGuard();
      onConfirmClick(change);
      setShowModal(false);
    }
  }, [
    checklist,
    description,
    disableSaveGuard,
    modalChecklist,
    modalDescription,
    modalTitle,
    onConfirmClick,
    title,
    titleError,
    descriptionError,
    checklistItemError,
  ]);

  const addChecklistItem = useCallback(
    (options?: {
      shouldFocusDraft?: boolean;
      shouldKeepDraftVisible?: boolean;
    }) => {
      const { shouldFocusDraft = true, shouldKeepDraftVisible = true } =
        options ?? {};
      if (
        modalChecklistInput &&
        modalChecklist.length < MAX_CHECKLIST_ITEMS &&
        !checklistItemError
      ) {
        setModalChecklist((prev) => [
          ...prev,
          { title: modalChecklistInput, completed: false },
        ]);
        setModalChecklistInput("");
        setShowChecklistError(false);
        setShowChecklistDraft(shouldKeepDraftVisible);

        if (typeof window !== "undefined" && shouldFocusDraft) {
          window.requestAnimationFrame(() => {
            const addChecklistButton = document.getElementById(
              ADD_CHECKLIST_BUTTON_ID
            );

            if (typeof addChecklistButton?.scrollIntoView === "function") {
              addChecklistButton.scrollIntoView({
                behavior: "smooth",
                block: "end",
              });
            }
            checklistInputRef.current?.focus();
          });
        }

        enableSaveGuard();
      }
    },
    [
      enableSaveGuard,
      modalChecklistInput,
      modalChecklist.length,
      checklistItemError,
    ]
  );

  const handleAddChecklistClick = useCallback(() => {
    if (disabled) {
      return;
    }

    if (!showChecklistDraft) {
      setShowChecklistDraft(true);

      if (typeof window !== "undefined") {
        window.requestAnimationFrame(() => {
          checklistInputRef.current?.focus();
        });
      }

      return;
    }

    if (!modalChecklistInput.trim() || checklistItemError) {
      checklistInputRef.current?.focus();
      return;
    }

    addChecklistItem();
  }, [
    addChecklistItem,
    disabled,
    checklistItemError,
    modalChecklistInput,
    showChecklistDraft,
  ]);

  const handleDeleteChecklistItem = useCallback(
    (index: number) => () => {
      setModalChecklist((prev) => prev.filter((_, i) => i !== index));
      enableSaveGuard();
    },
    [enableSaveGuard]
  );

  const handleChecklistDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (active.id !== over?.id) {
        const oldIndex = modalChecklist.findIndex(
          (item, index) => createChecklistItemId(item, index) === active.id
        );
        const newIndex = modalChecklist.findIndex(
          (item, index) => createChecklistItemId(item, index) === over?.id
        );

        if (oldIndex >= 0 && newIndex >= 0) {
          setModalChecklist((prev) => arrayMove(prev, oldIndex, newIndex));
          enableSaveGuard();
        }
      }
    },
    [enableSaveGuard, modalChecklist]
  );

  const handleChecklistInputKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        setShowChecklistError(true);

        if (!modalChecklistInput.trim()) {
          return;
        }

        addChecklistItem();
      }
    },
    [addChecklistItem, modalChecklistInput]
  );

  const handleListItemClick = useCallback(() => {
    setModalTitle(title);
    setModalDescription(description);
    setModalChecklist(checklist);
    setModalChecklistInput("");
    setShowChecklistError(false);
    setShowChecklistDraft(false);
    setShowModal(true);
  }, [checklist, description, title]);

  const handleTitleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setModalTitle(e.target.value);
      enableSaveGuard();
    },
    [enableSaveGuard]
  );

  const handleDescriptionChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      setModalDescription(e.target.value);
      enableSaveGuard();
    },
    [enableSaveGuard]
  );

  const handleChecklistInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const sanitizedValue = e.target.value.replace(/^\s+/, "");
      setModalChecklistInput(sanitizedValue);

      if (sanitizedValue.trim()) {
        setShowChecklistError(false);
      }
      enableSaveGuard();
    },
    [enableSaveGuard]
  );

  const handleChecklistInputBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const relatedTarget = e.relatedTarget as HTMLElement | null;

      if (
        relatedTarget?.id === ADD_CHECKLIST_BUTTON_ID ||
        relatedTarget?.dataset?.["testid"] === "remove-checklist-input-button"
      ) {
        return;
      }

      if (!modalChecklistInput.trim()) {
        setShowChecklistError(false);
        setShowChecklistDraft(false);
        return;
      }

      addChecklistItem({
        shouldFocusDraft: false,
        shouldKeepDraftVisible: false,
      });
    },
    [addChecklistItem, modalChecklistInput]
  );

  const handleClearChecklistInput = useCallback(() => {
    setModalChecklistInput("");
    setShowChecklistError(false);
    setShowChecklistDraft(false);
    checklistInputRef.current?.focus();
    enableSaveGuard();
  }, [enableSaveGuard]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor)
  );

  return (
    <>
      <ListItem
        onClick={handleListItemClick}
        title={t("taskDescriptionString")}
        disabled={disabled}
        description={title}
        addOn={listViewError ? <ErrorAddOn>{listViewError}</ErrorAddOn> : null}
        emphasised={title.length > 0}
        testId="custom-task-description-list-item"
      />
      <FixaModal visible={showModal} handleCloseBtn={handleCloseModalClick}>
        <FixaSheets
          labelledById="task-management-custom-task-description-header"
          header={
            <FixaModalHeader
              id="task-management-custom-task-description-header"
              closeBtnClick={handleCloseModalClick}
              title={t("taskDescriptionString")}
            />
          }
          footer={
            <StyledModalFooter>
              <FixaButton
                text={t("confirmString")}
                type="primary"
                fluid
                onClick={handleConfirmClick}
                data-testid="custom-task-description-confirm-button"
              />
            </StyledModalFooter>
          }
          fullSize
        >
          <FixaModalBody>
            <ModalColumn gap="medium">
              <InputWrapper error={titleError}>
                <FixaInputField
                  id="title"
                  type="text"
                  label={t("taskTitleString")}
                  value={modalTitle}
                  disabled={disabled}
                  onChange={handleTitleChange}
                  data-testid="custom-task-title-input"
                />
              </InputWrapper>
              <InputWrapper error={descriptionError}>
                <FixaTextArea
                  id="description"
                  label={t("description4String")}
                  value={modalDescription}
                  disabled={disabled}
                  onChange={handleDescriptionChange}
                  data-testid="custom-task-description-input"
                />
              </InputWrapper>
              <FixaText textStyle="Heading.XS">
                {t("createChecklistString")}
              </FixaText>
              <div>
                {modalChecklist.length > 0 ? (
                  <div className={styles["checklistItemsWrapper"]}>
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleChecklistDragEnd}
                      modifiers={[
                        restrictToVerticalAxis,
                        restrictToParentElement,
                      ]}
                    >
                      <SortableContext
                        items={modalChecklist.map((item, index) =>
                          createChecklistItemId(item, index)
                        )}
                        strategy={verticalListSortingStrategy}
                      >
                        {modalChecklist.map((item, index) => {
                          return (
                            <NewChecklistItem
                              key={createChecklistItemId(item, index)}
                              itemId={createChecklistItemId(item, index)}
                              title={item.title}
                              onDelete={handleDeleteChecklistItem(index)}
                            />
                          );
                        })}
                      </SortableContext>
                    </DndContext>
                  </div>
                ) : null}
                {showChecklistDraft ? (
                  <div className={styles["checklistDraftItem"]}>
                    <div className={styles["checklistDraftRow"]}>
                      <FixaSSRIcon paths={HandleIconPath} />
                      <input
                        id="checklist-input"
                        ref={checklistInputRef}
                        className={styles["checklistDraftInput"]}
                        aria-label={t("createChecklistString")}
                        value={modalChecklistInput}
                        disabled={disabled}
                        onChange={handleChecklistInputChange}
                        onBlur={handleChecklistInputBlur}
                        onKeyDown={handleChecklistInputKeyDown}
                        autoFocus
                      />
                      <FixaButton
                        ssrIcon={TrashCanIconPath}
                        iconOnly
                        type="tertiary"
                        size="xsmall"
                        onClick={handleClearChecklistInput}
                        disabled={disabled}
                        data-testid="remove-checklist-input-button"
                      />
                    </div>
                    {checklistItemError ? (
                      <div className={styles["checklistValidationErrorRow"]}>
                        <ErrorAddOn>{checklistItemError}</ErrorAddOn>
                      </div>
                    ) : null}
                  </div>
                ) : null}
                <div
                  className={styles["addChecklistButton"]}
                  id={ADD_CHECKLIST_BUTTON_ID}
                  onClick={handleAddChecklistClick}
                  role="button"
                  tabIndex={disabled ? -1 : 0}
                  aria-disabled={disabled}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleAddChecklistClick();
                    }
                  }}
                >
                  <FixaSSRIcon paths={SmallPlusIconPath} />
                  <FixaText
                    textStyle="Body.S"
                    className={styles["addChecklistButtonText"]}
                  >
                    {t("addChecklistItemString")}
                  </FixaText>
                </div>
              </div>
            </ModalColumn>
          </FixaModalBody>
        </FixaSheets>
      </FixaModal>
    </>
  );
}
